import React from 'react';
import classnames from 'classnames';
import stats, {getStatusNameByAccuracyForStudent, getBucketByAccuracy} from 'lib/StatsUtil';
import sessionStore from 'client/Session/SessionStore';
import {GuideModelV1} from 'resources/augmented/Guide/GuideModel.v1';

import './accuracy-badge.scss';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/FeatureFlag.react';
import {Text, WithTooltip} from '@albert-io/atomic';

interface Props {
  className?: string;
  guideLevel: GuideModelV1;
  metadata: React.ReactNode;
}

const MIN_REQUIRED_QUESTIONS = 5;
const REQUIRED_PERCENTAGE = 0.1; // 10%

function AccuracyBadge({className, guideLevel, metadata}: Props) {
  const {isFeatureEnabled: removeMasteryLevelFF, isLoading} =
    useIsFeatureFlagEnabled('remove_mastery_levels');

  if (isLoading) {
    return null;
  }

  const topic = guideLevel;

  if (removeMasteryLevelFF) {
    const topicAccuracy = stats.percentage(
      topic.getMetaStudentCountOfCorrectGuesses(),
      topic.getMetaCountOfUniqueQuestionsAnswered()
    );

    const color = getBucketByAccuracy(topicAccuracy).COLOR;

    // If a user is logged in they see one of three possible statuses:
    // 1. a 'Learning' label showing them that they are required to answer more questions before they can see their accuracy
    // 2. a label showing them their raw accuracy
    // 3. the default "Not started" label if they have answered nothing.

    if (topic.getMetaCountOfUniqueQuestionsAnswered() === 0) {
      return (
        <div className='u-display_flex u-align_items_center u-text-wrap_nowrap u-pad-r_1'>
          {!guideLevel.isTheme() ? (
            <Text size='m' className='u-pad_1'>
              {metadata}
            </Text>
          ) : null}
          <Text size='m' className='u-pad_1'>
            Not Started
          </Text>
        </div>
      );
    }

    // If a user has not answered 10% of the guide's questions, they will not be shown their accuracy,
    // but will instead see a Learning badge with a tooltip letting them know
    // how many remaining questions they have to answer.

    // 10% or 5 -- whatever is smaller. Rounding the percentage value so we get less odd results.
    // ex 10% of 17 is 1.7, but we can round that to be 2 so the user needs to answer 2 questions and not 1
    const requiredAnswered = Math.min(
      Math.round(topic.getMetaCountOfQuestions() * REQUIRED_PERCENTAGE),
      MIN_REQUIRED_QUESTIONS
    );
    const isLearning = topic.getMetaCountOfUniqueQuestionsAnswered() < requiredAnswered;

    return (
      <div className='u-display_flex u-align_items_center u-text-wrap_nowrap u-pad-r_1'>
        {!guideLevel.isTheme() ? (
          <Text size='m' className='u-pad_1'>
            {metadata}
          </Text>
        ) : null}
        {isLearning ? (
          <WithTooltip
            content={`You need to answer at least ${requiredAnswered} questions before you can see your accuracy.`}
            placement='bottom'
            display='flex'
          >
            <Text
              size='m'
              className='accuracy-badge__accuracy u-pad_1 u-border-l u-border-color_primary'
              style={{
                color: `var(--${color}-bold)`
              }}
            >
              Learning
            </Text>
          </WithTooltip>
        ) : (
          <Text
            size='m'
            className='accuracy-badge__accuracy u-pad_1 u-border-l u-border-color_primary'
            style={{
              color: `var(--${color}-bold)`
            }}
          >{`${topicAccuracy}% Accuracy`}</Text>
        )}
      </div>
    );
  }

  const generateBadge = ({
    percentage = null,
    status
  }: {
    percentage?: number | null;
    status: string;
  }) => {
    const classNames = classnames('accuracy-badge__wrapper', className);
    const formattedStatus = status.replace(' ', '-').toLowerCase();
    return (
      <div className='u-display_flex u-align_items_center u-text-wrap_nowrap'>
        {!guideLevel.isTheme() ? (
          <Text size='m' className='u-pad_1'>
            {metadata}
          </Text>
        ) : null}
        <div className={classNames}>
          <span
            className={`accuracy-badge accuracy-badge--status accuracy-badge--${formattedStatus}`}
          >
            {status}
          </span>
          {percentage !== null ? (
            <span
              className={`accuracy-badge accuracy-badge--percentage  accuracy-badge--${formattedStatus}`}
            >
              {`${percentage}%`}
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  // The default badge is shown to users that are not logged in
  const defaultBadge = generateBadge({
    status: 'Not started'
  });

  if (!sessionStore.hasValidSession()) {
    return defaultBadge;
  }

  // If a user is logged in they see one of three possible badges:
  // 1. a badge showing them that they are required to answer more questions before they can see their accuracy
  // 2. a badge showing them their raw accuracy
  // 3. the default "Not started" badge if they have answered nothing.

  if (topic.getMetaCountOfUniqueQuestionsAnswered() === 0) {
    return defaultBadge;
  }

  // If a user has not 10% of the guide's questions, they will not be shown their accuracy,
  // but will instead see a Learning badge with a tooltip letting them know
  // how many remaining questions they have to answer.

  // 10% or 5 -- whatever is smaller. Rounding the percentage value so we get less odd results.
  // ex 10% of 17 is 1.7, but we can round that to be 2 so the user needs to answer 2 questions and not 1
  const requiredAnswered = Math.min(Math.round(topic.getMetaCountOfQuestions() * 0.1), 5);

  if (topic.getMetaCountOfUniqueQuestionsAnswered() < requiredAnswered) {
    return generateBadge({
      percentage: 0,
      status: getStatusNameByAccuracyForStudent(0)
    });
  }

  // Finally if we make it this far, a student has their raw accuracy computed and shown to them
  // with the appropriate badge text and tooltip
  const topicRawAccuracy = stats.percentage(
    topic.getMetaStudentCountOfCorrectGuesses(),
    topic.getMetaCountOfUniqueQuestionsAnswered()
  );

  return generateBadge({
    percentage: topicRawAccuracy,
    status: getStatusNameByAccuracyForStudent(topicRawAccuracy)
  });
}

export default AccuracyBadge;
